import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { MdPlayArrow, MdNotificationsActive } from 'react-icons/md';
import toast from 'react-hot-toast';
import useAppQuery from 'hooks/useAppQuery';
import usePostAxiosData from 'hooks/usePostAxiosData';
import { useSchedulerConfig } from 'hooks/useNotificationScheduler';
import { cronToFrench } from 'utils/cronUtils';
import { config } from 'variables/config';

export default function SchedulerControlPanel({ onBatchTriggered }) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: settingsData, refetch: refetchSettings } = useAppQuery(
    `${config.app.api_url}/settings`
  );
  const isActive = settingsData?.data?.activeAutoEnterpriseNotifier ?? false;

  const { data: schedulerConfigData } = useSchedulerConfig();
  const schedulerConfig = schedulerConfigData?.data;

  const [enableLoading, enableNotifier] = usePostAxiosData(
    'settings/enable-enterprise-notifier',
    'PUT'
  );
  const [disableLoading, disableNotifier] = usePostAxiosData(
    'settings/disable-enterprise-notifier',
    'PUT'
  );
  const [triggerLoading, triggerBatch] = usePostAxiosData(
    'schedulers/notify-enterprise-employees-status',
    'PUT'
  );

  const handleToggle = async (e) => {
    const checked = e.target.checked;
    if (checked) {
      await enableNotifier({});
      toast.success('Notifications automatiques activées');
    } else {
      await disableNotifier({});
      toast.success('Notifications automatiques désactivées');
    }
    refetchSettings();
  };

  const handleTrigger = async () => {
    setConfirmOpen(false);
    await triggerBatch({});
    toast.success('Batch de notification déclenché avec succès');
    if (onBatchTriggered) onBatchTriggered();
  };

  const toggleLoading = enableLoading || disableLoading;

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {/* Toggle auto-notifier */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <MdNotificationsActive size={24} color={isActive ? '#38A169' : '#A0AEC0'} />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Notifications automatiques
            </Typography>
            {schedulerConfig && (
              <Typography variant="caption" color="text.secondary">
                {cronToFrench(schedulerConfig.cronExpression)} — Batch :{' '}
                {schedulerConfig.batchSize} entreprises — max{' '}
                {schedulerConfig.maxNoEmailAttempts} tentatives sans email
              </Typography>
            )}
          </Box>
          <Chip
            label={isActive ? 'Actif' : 'Désactivé'}
            color={isActive ? 'success' : 'default'}
            size="small"
            sx={{ ml: 1 }}
          />
          {toggleLoading ? (
            <CircularProgress size={20} />
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={handleToggle}
                  color="success"
                />
              }
              label=""
              sx={{ m: 0 }}
            />
          )}
        </Box>

        {/* Bouton déclenchement manuel */}
        <Button
          variant="contained"
          color="primary"
          startIcon={
            triggerLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <MdPlayArrow />
            )
          }
          disabled={triggerLoading}
          onClick={() => setConfirmOpen(true)}
        >
          Déclencher un batch maintenant
        </Button>
      </Box>

      {/* Dialog de confirmation */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmer le déclenchement</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cette action va déclencher immédiatement un batch de notifications pour les entreprises
            éligibles. Le cooldown de 7 jours sera respecté.
            <br />
            <br />
            Voulez-vous continuer ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">
            Annuler
          </Button>
          <Button onClick={handleTrigger} variant="contained" color="primary" autoFocus>
            Déclencher
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
